/**
 * Artwork the loading screen itself draws, plus the land-page ambience and the loose root-level
 * images that belong to no folder.
 *
 * These live in their own module rather than inside the components that use them so
 * `vercelBlobPreloadUrls.ts` can gate them without importing React.
 */

const BLOB = 'https://s0swaypl0zqnzq8t.public.blob.vercel-storage.com'

/**
 * Cross-fading cards behind the loading screen (`LoadingScreenBackdrop`).
 *
 * Gating these looks circular — they are on screen *while* the gate runs, so the first visit
 * cannot benefit. It pays off everywhere else: repeat visits, and every per-route loading screen,
 * which show the same backdrop and would otherwise fetch it cold each time.
 */
export const LOADING_SCREEN_CARD_URLS: readonly string[] = Array.from(
  { length: 9 },
  (_, i) => `${BLOB}/main/loading-cards/loading-${i + 1}.webp`,
)

/**
 * Ambient animation on the land page and its guide.
 *
 * Animated files are normally kept out of the gate on the grounds that a screen is usable before
 * its decoration arrives. These four are the exception by explicit request — the land page is
 * where players spend their time, and villagers popping into existence a second late is the kind
 * of thing that reads as a broken build. ~5.4 MB, and `sandwatch.gif` alone is 3.55 MB.
 */
export const LAND_AMBIENT_ANIMATION_URLS: readonly string[] = [
  `${BLOB}/_merged_/person1.gif`,
  `${BLOB}/_merged_/talking_people.gif`,
  `${BLOB}/_merged_/working_people.gif`,
  'https://nsdlsrcxj85lsgpu.public.blob.vercel-storage.com/sandwatch.gif',
]

/**
 * Root-level images with no folder to group them under. Small individually, and every one of them
 * is referenced by a screen inside the gated surface.
 */
export const MISC_ROOT_ASSET_URLS: readonly string[] = [
  `${BLOB}/ability.webp`,
  `${BLOB}/back.webp`,
  `${BLOB}/bg.webp`,
  `${BLOB}/border-photo.webp`,
  `${BLOB}/character-selection-modal-bg.webp`,
  `${BLOB}/stone.webp`,
  `${BLOB}/water-drop.webp`,
]

/** Everything in this module, for the gate. */
export const LOADING_AND_AMBIENT_PRELOAD_URLS: readonly string[] = [
  ...LOADING_SCREEN_CARD_URLS,
  ...LAND_AMBIENT_ANIMATION_URLS,
  ...MISC_ROOT_ASSET_URLS,
]
